import { useCallback, useEffect, useState } from 'react';

const PINNED_KEY = 'scicomic-sidebar-pinned';
const COLLAPSED_KEY = 'scicomic-sidebar-collapsed';

const readStoredBoolean = (key: string, fallback: boolean) => {
  const value = window.localStorage.getItem(key);
  return value === null ? fallback : value === 'true';
};

export const useSidebarState = () => {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia('(max-width: 900px)').matches,
  );
  const [isPinned, setIsPinned] = useState(() =>
    readStoredBoolean(PINNED_KEY, false),
  );
  const [isCollapsed, setIsCollapsed] = useState(() =>
    readStoredBoolean(COLLAPSED_KEY, false),
  );
  const [isHoveringCollapsedSidebar, setIsHoveringCollapsedSidebar] =
    useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 900px)');
    const handleChange = (event: MediaQueryListEvent) =>
      setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (isMobile) return;

    const nextCollapsed = !isPinned;
    setIsCollapsed(nextCollapsed);
    window.localStorage.setItem(COLLAPSED_KEY, String(nextCollapsed));
  }, [isMobile, isPinned]);

  const togglePinned = useCallback(() => {
    setIsPinned((current) => {
      const next = !current;
      const nextCollapsed = !next;

      window.localStorage.setItem(PINNED_KEY, String(next));
      window.localStorage.setItem(COLLAPSED_KEY, String(nextCollapsed));
      setIsCollapsed(nextCollapsed);
      setIsHoveringCollapsedSidebar(false);

      return next;
    });
  }, []);

  const handleSidebarMouseEnter = useCallback(() => {
    if (!isMobile && !isPinned && isCollapsed) {
      setIsHoveringCollapsedSidebar(true);
    }
  }, [isCollapsed, isMobile, isPinned]);

  const handleSidebarMouseLeave = useCallback(() => {
    setIsHoveringCollapsedSidebar(false);
  }, []);

  const effectiveIsCollapsed = isMobile ? false : isCollapsed;
  const isSidebarExpanded =
    isMobile || !effectiveIsCollapsed || isHoveringCollapsedSidebar;

  return {
    isCollapsed: effectiveIsCollapsed,
    isSidebarExpanded,
    isPinned,
    sidebarHoverHandlers: {
      onMouseEnter: handleSidebarMouseEnter,
      onMouseLeave: handleSidebarMouseLeave,
    },
    togglePinned,
  };
};
